<template>
  <BaseModal
    :model-value="modelValue"
    size="md"
    @update:model-value="emit('update:modelValue', $event)"
  >
    <template #header>
      <div class="modal-hero" :class="{ 'disclaimer-active': showDisclaimer }">
        <img
          src="~/assets/images/modals/company-page-creation/background-image.png"
          alt="Создание страницы компании"
          class="hero-image"
        />
        <button class="close-button" @click="onClose">
          <BaseIconSvoi icon="essential/xmark-clear" alt="delete" />
        </button>
        <DisclaimerToast v-if="showDisclaimer" @close="closeDisclaimer" />
      </div>
    </template>

    <template #close>
      <div class="modal-company-page-creation">
        <div class="modal-content">
          <div class="modal-header">
            <h2 class="title">
              Создавайте и редактируйте страницу своей компании прямо в личном кабинете работодателя
            </h2>
            <p class="description">
              Теперь каждый работодатель может самостоятельно создавать и обновлять свою страницу на
              платформе «Я в Агро» — быстро, удобно и без обращения в поддержку. Новая функция
              доступна прямо в личном кабинете.
            </p>
          </div>

          <FeaturesList :features="features" />

          <div class="info-note">
            <p class="note-text">
              📌 Ваша страница — это визитная карточка компании. Актуальная информация помогает
              привлекать больше откликов и усиливать HR-бренд.
            </p>
          </div>

          <div class="info-banner theme-blue">
            <p class="banner-text">
              Новым компаниям нужно зарегистрироваться на сайте. После модерации функция создания
              страницы станет доступна в личном кабинете.
            </p>
          </div>

          <div class="modal-actions">
            <BaseButton name="Создать страницу" size="xxl" @click="createCompanyPage" />
          </div>
        </div>
      </div>
    </template>
  </BaseModal>
</template>

<script lang="ts" setup>
  import { BaseModal } from '~/components/base-ui/modal'
  import { BaseButton } from '~/components/base-ui/buttons'
  import { BaseIconSvoi } from '~/components/base-ui/icon'
  import { FeaturesList, DisclaimerToast } from '~/components/modals/company-page-creation/index'
  import { useAuthModal } from '~/composables/use-auth-modal'
  import { useUserStore } from '~/store/user'
  import pen from '~/assets/icons/pen.svg'
  import checkBadge from '~/assets/icons/check-badge.svg'
  import thumbLikeDislike from '~/assets/icons/thumb-like-dislike.svg'
  import updateSquare from '~/assets/icons/update-square.svg'
  import cards from '~/assets/icons/cards.svg'

  interface Props {
    modelValue?: boolean
  }

  withDefaults(defineProps<Props>(), {
    modelValue: false,
  })

  const emit = defineEmits<{
    (e: 'update:modelValue', value: boolean): void
    (e: 'on-close'): void
  }>()
  const { isAuth } = useAuth()
  const { openModal } = useAuthModal()
  const router = useRouter()
  const { hasRole } = useUserStore()
  const showDisclaimer = ref(false)

  const features = [
    {
      icon: pen,
      text: 'Изменяйте описание компании',
    },
    {
      icon: checkBadge,
      text: 'Загружайте актуальный логотип',
    },
    {
      icon: thumbLikeDislike,
      text: 'Рассказывайте о бонусах, стажировках и корпоративной культуре',
      fullWidth: true,
    },
    {
      icon: updateSquare,
      text: 'Обновляйте контактную информацию',
    },
    {
      icon: cards,
      text: 'Публикуйте и редактируйте вакансии',
    },
  ]

  const closeDisclaimer = () => {
    showDisclaimer.value = false
  }

  const onClose = () => {
    emit('on-close')
  }

  const createCompanyPage = () => {
    if (!isAuth.value) {
      openModal({ backUrl: '/account?source=company-page-creation' })
      emit('on-close')
      return
    }

    if (hasRole('company') || hasRole('vacancy_admin')) {
      router.push('/account/employer-page')
      emit('on-close')
      return
    }

    showDisclaimer.value = true
    setTimeout(() => {
      showDisclaimer.value = false
    }, 3000)
  }
</script>

<style lang="scss" scoped>
  .modal-hero {
    position: relative;
    width: 100%;
    height: 260px;
    overflow: visible;
    margin: 0;
    padding: 0;

    > .hero-image {
      width: 100%;
      height: 100%;
      display: block;
      object-fit: cover;
    }

    > .close-button {
      position: absolute;
      top: 16px;
      right: 16px;
      z-index: 10;
      width: 30px;
      height: 30px;
      border: none;
      border-radius: 50%;
      background: #fff;
      color: #000;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all $trans-default;

      &:hover {
        background: #f5f5f5;
      }

      &:active {
        transform: scale(0.95);
      }
    }

    &.disclaimer-active {
      > .close-button {
        @media (width <= 480px) {
          display: none;
        }
      }
    }

    @media (width <= 1024px) {
      height: 240px;
    }

    @media (width <= 480px) {
      height: 260px;
    }
  }

  .modal-company-page-creation {
    position: relative;
    background: $color-default-white;

    @include vp-767 {
      overflow: auto;
      margin: 0 -24px;
      max-height: 48vh;
    }

    > .modal-content {
      width: 100%;
      box-sizing: border-box;

      > .modal-header {
        margin-bottom: 24px;
        width: 100%;

        > .title {
          font-weight: 500;
          font-size: 24px;
          line-height: 32px;
          margin: 0 0 12px;
          width: 100%;

          @include vp-767 {
            font-size: 20px;
            line-height: 28px;
          }
        }

        > .description {
          font-size: 16px;
          line-height: 24px;
          margin: 0;
          width: 100%;
        }
      }

      > .info-note {
        margin-bottom: 16px;
        width: 100%;

        > .note-text {
          font-size: 16px;
          line-height: 24px;
          margin: 0;
          width: 100%;
        }
      }

      > .info-banner {
        padding: 16px;
        border-radius: 16px;
        margin-bottom: 24px;
        width: 100%;
        box-sizing: border-box;

        > .banner-text {
          font-weight: 500;
          font-size: 14px;
          line-height: 22px;
          margin: 0;
          width: 100%;
        }

        &.theme-blue {
          background: $main-back-color;

          > .banner-text {
            color: $color-bright-blue;
          }
        }
      }

      > .modal-actions {
        display: flex;
        justify-content: center;
        margin-top: 32px;
        width: 100%;

        @include vp-767 {
          margin-top: 24px;
        }

        > .svoi-button {
          background-color: $color-kiwi-50;
        }
      }
    }
  }
</style>
